import type { Method } from 'axios';
import axios, { isAxiosError } from 'axios';
import { getItem } from '../../utils/storage';
import { handleLegacyApiResponse, handleLegacyApiError } from './response';
import { API_VERSION } from '../../config/app';
import type { RegionEnum } from '../../config/region';
import { EnvironmentEnum, getServerConfig } from '../../config/region';
import isElectron from 'is-electron';
import { updateSession } from '../../api/sessions';
import { syncActiveTenantFromGlobalTokens } from '../../containers/Organizations/OrganizationsSlice/upsertTenantSession';
import { getCoreAuthHeader } from '../../utils/getCoreAuthHeader';

export const getApiBaseUrl = (customUrl?: string) => {
  if (customUrl) {
    return `${customUrl}/api`;
  }
  return `${getResolvedApiUrl()}/api`;
};

/**
 * Centralized function to get the resolved API URL based on environment, platform, and configuration
 * This function implements the logic to choose between apiUrl and apiUrlCom
 */
export const getResolvedApiUrl = (overrideRegion?: RegionEnum): string => {
  const { apiUrlCom, apiUrl } = getServerConfig(overrideRegion);
  const envType = process.env.REACT_APP_ENV_NAME as EnvironmentEnum;

  if (envType === EnvironmentEnum.PROD) {
    // Desktop keep using .se api URLs to avoid losing current logged session for Br region, that would be the a new app
    if (isElectron()) return apiUrl;
    // if apiUrlCom is set (configured for Swedish servers to separate between web and desktop), use .com URL
    if (apiUrlCom) return apiUrlCom;
    return apiUrl;
  }
  return apiUrl;
};

export let BASE_URL = getApiBaseUrl();

export const changeBaseUrl = (_url: string) => {
  BASE_URL = getApiBaseUrl(_url);
};

const csrf = getItem('csrf');

let isInterceptorRegistered = false;
const retriedRequests = new WeakSet<object>();

export const initializeAxiosInterceptor = () => {
  if (isInterceptorRegistered) return;

  isInterceptorRegistered = true;

  axios.interceptors.response.use(
    response => {
      return response;
    },
    async (error: unknown) => {
      if (!isAxiosError(error)) {
        return Promise.reject(error);
      }

      const originalRequest = error.config;

      if (!originalRequest) {
        return Promise.reject(error);
      }

      // Handle expired access token - attempt to refresh
      if (
        error.response?.status === 401 &&
        error.response?.data?.Code === 'unauthorized-access' &&
        !retriedRequests.has(originalRequest)
      ) {
        retriedRequests.add(originalRequest);

        const sessionId = getItem('coreApiSessionId');
        const refreshToken = getItem('coreApiRefreshToken');

        if (!sessionId || !refreshToken) {
          return Promise.reject(error);
        }

        const session = await updateSession({
          sessionId,
          refreshToken,
        });

        if (session.data) {
          syncActiveTenantFromGlobalTokens({
            accessToken: session.data.data.accessToken,
            refreshToken: session.data.data.refreshToken,
            sessionId: session.data.data.id,
          });
          originalRequest.headers['authorization'] = `Bearer ${session.data.data.accessToken}`;
          return axios(originalRequest);
        }
      }

      return Promise.reject(error);
    }
  );
};

const addVersion = (version?: string): string => {
  return version ? version + '/' : '';
};

const getAll = <T>(resource: string, version?: string): Promise<T> => {
  return axios
    .get(`${BASE_URL}/${addVersion(version)}${resource}`, {
      withCredentials: true,
      headers: getCoreAuthHeader(),
    })
    .then(handleLegacyApiResponse)
    .catch(handleLegacyApiError);
};

const getSingle = <T>(resource: string, id: number, version?: string): Promise<T> => {
  return axios
    .get(`${BASE_URL}/${addVersion(version)}${resource}/${id}`, {
      withCredentials: true,
      headers: getCoreAuthHeader(),
    })
    .then(handleLegacyApiResponse)
    .catch(handleLegacyApiError);
};

const post = <T>(resource: string, model: object, version?: string): Promise<T> => {
  return axios
    .post(`${BASE_URL}/${addVersion(version)}${resource}`, model, {
      withCredentials: true,
      headers: {
        ...getCoreAuthHeader(),
        'X-XSRF-Token': csrf,
      },
    })
    .then(handleLegacyApiResponse)
    .catch(handleLegacyApiError);
};

const put = <T>(resource: string, model: object, version?: string): Promise<T> => {
  return axios
    .put(`${BASE_URL}/${addVersion(version)}${resource}`, model, {
      withCredentials: true,
      headers: {
        ...getCoreAuthHeader(),
        'X-XSRF-Token': csrf,
      },
    })
    .then(handleLegacyApiResponse)
    .catch(handleLegacyApiError);
};

const patch = <T>(resource: string, model: object, version?: string): Promise<T> => {
  return axios
    .patch(`${BASE_URL}/${addVersion(version)}${resource}`, model, {
      withCredentials: true,
      headers: {
        ...getCoreAuthHeader(),
        'X-XSRF-Token': csrf,
      },
    })
    .then(handleLegacyApiResponse)
    .catch(handleLegacyApiError);
};

const remove = <T>(resource: string, id: number, version?: string): Promise<T> => {
  return axios
    .delete(`${BASE_URL}/${addVersion(version)}${resource}`, {
      data: {
        id,
      },
      withCredentials: true,
      headers: {
        ...getCoreAuthHeader(),
        'X-XSRF-Token': csrf,
      },
    })
    .then(handleLegacyApiResponse)
    .catch(handleLegacyApiError);
};

const performExtra = <T>(
  method: Method,
  resource: string,
  extraResource: string,
  returnErrorResponseData?: boolean,
  id?: number,
  modal?: object,
  params?: object,
  buffer?: boolean,
  headers?: any,
  version?: string,
  baseUrl?: string
): Promise<T> => {
  return axios
    .request({
      baseURL: baseUrl || BASE_URL,
      method,
      data: modal,
      params: params,
      url: `${addVersion(version)}${resource}/${id ? id + '/' : ''}${extraResource}`,
      withCredentials: true,
      responseType: buffer ? 'arraybuffer' : 'json',
      headers: {
        ...getCoreAuthHeader(),
        'cosafe-api-version': API_VERSION,
        ...headers,
      },
    })
    .then(res => {
      return handleLegacyApiResponse(res);
    })
    .catch(error => handleLegacyApiError(error, returnErrorResponseData));
};

export const apiProvider = {
  getAll,
  getSingle,
  post,
  put,
  patch,
  remove,
  performExtra,
};
